import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaShieldAlt } from 'react-icons/fa';
import { 
  collection, 
  query, 
  orderBy, 
  onSnapshot, 
  doc, 
  updateDoc, 
  Timestamp,
  getDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import '../admin.css';
import './Reports.css';

interface Report {
  id: string;
  reporterId: string;
  reporterName: string;
  reporterRole: string;
  reportedUserId: string;
  reportedUserName: string;
  reportType: string;
  description: string;
  conversationId?: string;
  messageId?: string;
  organizationCode: string;
  status: 'pending' | 'reviewed' | 'resolved';
  createdAt: any;
  updatedAt: any;
  reviewedBy?: string;
  reviewedAt?: any;
  adminNotes?: string;
}

interface UserBanInfo {
  bannedFromChat: boolean;
  chatBanUntil?: any;
  chatBanReason?: string;
}

const Reports: React.FC = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterOrg, setFilterOrg] = useState<string>('all');
  const [adminNotes, setAdminNotes] = useState('');
  const [banDuration, setBanDuration] = useState('7'); // days
  const [userReportHistory, setUserReportHistory] = useState<Report[]>([]);
  const [userBanInfo, setUserBanInfo] = useState<UserBanInfo | null>(null);

  useEffect(() => {
    const q = query(collection(db, 'reports'), orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const reportsData: Report[] = [];
      snapshot.forEach((doc) => {
        reportsData.push({ id: doc.id, ...doc.data() } as Report);
      });
      setReports(reportsData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const loadUserHistory = async (userId: string) => {
    const userReports = reports.filter(r => r.reportedUserId === userId);
    setUserReportHistory(userReports);

    // Load ban info
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserBanInfo({
          bannedFromChat: userData.bannedFromChat || false,
          chatBanUntil: userData.chatBanUntil,
          chatBanReason: userData.chatBanReason,
        });
      }
    } catch (error) {
      console.error('Error loading user ban info:', error);
    }
  };

  const handleReviewReport = async (reportId: string, status: string) => {
    try {
      await updateDoc(doc(db, 'reports', reportId), {
        status,
        reviewedBy: 'admin', // Replace with actual admin ID
        reviewedAt: Timestamp.now(),
        adminNotes,
        updatedAt: Timestamp.now(),
      });
      setAdminNotes('');
      setSelectedReport(null);
      alert(`Report ${status} successfully!`);
    } catch (error) {
      console.error('Error updating report:', error);
      alert('Failed to update report');
    }
  };

  const handleBanUser = async (userId: string, userName: string) => {
    if (!window.confirm(`Are you sure you want to ban ${userName} from chatting for ${banDuration} days?`)) {
      return;
    }

    try {
      const banUntil = new Date();
      banUntil.setDate(banUntil.getDate() + parseInt(banDuration));

      await updateDoc(doc(db, 'users', userId), {
        bannedFromChat: true,
        chatBanUntil: Timestamp.fromDate(banUntil),
        chatBanReason: selectedReport?.reportType || 'Violation of community guidelines',
        bannedBy: 'admin', // Replace with actual admin ID
        bannedAt: Timestamp.now(),
      });

      // Send notification (handled by Flutter app via Firestore trigger or check)
      alert(`User ${userName} has been banned from chatting until ${banUntil.toDateString()}`);
      
      // Reload user info
      if (selectedReport) {
        loadUserHistory(userId);
      }
    } catch (error) {
      console.error('Error banning user:', error);
      alert('Failed to ban user');
    }
  };

  const handleUnbanUser = async (userId: string, userName: string) => {
    if (!window.confirm(`Are you sure you want to unban ${userName}?`)) {
      return;
    }

    try {
      await updateDoc(doc(db, 'users', userId), {
        bannedFromChat: false,
        chatBanUntil: null,
        chatBanReason: null,
      });

      alert(`User ${userName} has been unbanned from chatting`);
      
      // Reload user info
      if (selectedReport) {
        loadUserHistory(userId);
      }
    } catch (error) {
      console.error('Error unbanning user:', error);
      alert('Failed to unban user');
    }
  };

  const filteredReports = reports.filter(report => {
    if (filterStatus !== 'all' && report.status !== filterStatus) return false;
    if (filterOrg !== 'all' && report.organizationCode !== filterOrg) return false;
    return true;
  });

  const organizations = [...new Set(reports.map(r => r.organizationCode))];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return '#ff9800';
      case 'reviewed': return '#2196f3';
      case 'resolved': return '#4caf50';
      default: return '#757575';
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="admin-theme reports-admin">
        <div className="reports-loading">
          <div className="reports-spinner"></div>
          <span>Loading reports...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-theme reports-admin">
      <div className="reports-shell">
        <div className="reports-topbar">
          <button className="back-to-admin" onClick={() => navigate('/admin')}>
            <FaArrowLeft /> Back to admin
          </button>
          <div className="reports-brand">
            <span className="reports-brand-mark">P</span>
            PEAR <span>ADMIN</span>
          </div>
        </div>

        <div className="reports-header">
          <div>
            <h1><FaShieldAlt /> User Reports & Safety</h1>
            <p>Review user reports, moderate chat access, and resolve safety cases.</p>
          </div>
        </div>

        <div className="reports-stats">
          <div className="stat-card">
            <h3>{reports.filter(r => r.status === 'pending').length}</h3>
            <p>Pending</p>
          </div>
          <div className="stat-card">
            <h3>{reports.filter(r => r.status === 'reviewed').length}</h3>
            <p>Reviewed</p>
          </div>
          <div className="stat-card">
            <h3>{reports.filter(r => r.status === 'resolved').length}</h3>
            <p>Resolved</p>
          </div>
        </div>

        <div className="reports-filters">
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="reviewed">Reviewed</option>
            <option value="resolved">Resolved</option>
          </select>

          <select value={filterOrg} onChange={(e) => setFilterOrg(e.target.value)}>
            <option value="all">All Organizations</option>
            {organizations.map(org => (
              <option key={org} value={org}>{org}</option>
            ))}
          </select>
        </div>

        <div className="reports-content">
          <div className="reports-list">
            {filteredReports.length === 0 ? (
              <div className="reports-empty">No reports match the current filters.</div>
            ) : filteredReports.map(report => (
              <div
                key={report.id}
                className={`report-card ${selectedReport?.id === report.id ? 'selected' : ''}`}
                onClick={() => {
                  setSelectedReport(report);
                  loadUserHistory(report.reportedUserId);
                }}
              >
                <div className="report-card-header">
                  <span className="report-type">{report.reportType}</span>
                  <span
                    className="report-status"
                    style={{ backgroundColor: getStatusColor(report.status) }}
                  >
                    {report.status}
                  </span>
                </div>
                <div className="report-card-body">
                  <p><strong>Reported User:</strong> {report.reportedUserName}</p>
                  <p><strong>Reporter:</strong> {report.reporterName} ({report.reporterRole})</p>
                  <p><strong>Organization:</strong> {report.organizationCode}</p>
                  <p className="report-description">{report.description}</p>
                  <p className="report-date">{formatDate(report.createdAt)}</p>
                </div>
              </div>
            ))}
          </div>

          {selectedReport ? (
            <div className="report-detail">
              <h2>Report Details</h2>
            
            <div className="detail-section">
              <h3>Report Information</h3>
              <p><strong>Report ID:</strong> {selectedReport.id}</p>
              <p><strong>Type:</strong> {selectedReport.reportType}</p>
              <p><strong>Status:</strong> {selectedReport.status}</p>
              <p><strong>Description:</strong> {selectedReport.description}</p>
              <p><strong>Created:</strong> {formatDate(selectedReport.createdAt)}</p>
              {selectedReport.conversationId && (
                <p><strong>Conversation ID:</strong> {selectedReport.conversationId}</p>
              )}
            </div>

            <div className="detail-section">
              <h3>Reported User</h3>
              <p><strong>Name:</strong> {selectedReport.reportedUserName}</p>
              <p><strong>ID:</strong> {selectedReport.reportedUserId}</p>
              <p><strong>Organization:</strong> {selectedReport.organizationCode}</p>
              
              {userBanInfo && (
                <div className="ban-info">
                  <p><strong>Chat Status:</strong> {userBanInfo.bannedFromChat ? '🚫 BANNED' : '✅ Active'}</p>
                  {userBanInfo.bannedFromChat && (
                    <>
                      <p><strong>Ban Until:</strong> {formatDate(userBanInfo.chatBanUntil)}</p>
                      <p><strong>Ban Reason:</strong> {userBanInfo.chatBanReason}</p>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="detail-section">
              <h3>Report History ({userReportHistory.length} total reports)</h3>
              <div className="history-list">
                {userReportHistory.slice(0, 5).map(hist => (
                  <div key={hist.id} className="history-item">
                    <p>{hist.reportType} - {hist.status}</p>
                    <p className="history-date">{formatDate(hist.createdAt)}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="detail-section">
              <h3>Actions</h3>
              
              <textarea
                placeholder="Admin notes..."
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                rows={3}
              />

              <div className="action-buttons">
                {selectedReport.status === 'pending' && (
                  <button 
                    className="btn btn-primary"
                    onClick={() => handleReviewReport(selectedReport.id, 'reviewed')}
                  >
                    Mark as Reviewed
                  </button>
                )}
                
                <button 
                  className="btn btn-success"
                  onClick={() => handleReviewReport(selectedReport.id, 'resolved')}
                >
                  Mark as Resolved
                </button>

                <div className="ban-controls">
                  <select 
                    value={banDuration} 
                    onChange={(e) => setBanDuration(e.target.value)}
                  >
                    <option value="1">1 Day</option>
                    <option value="3">3 Days</option>
                    <option value="7">7 Days</option>
                    <option value="14">14 Days</option>
                    <option value="30">30 Days</option>
                  </select>
                  
                  {userBanInfo?.bannedFromChat ? (
                    <button 
                      className="btn btn-warning"
                      onClick={() => handleUnbanUser(selectedReport.reportedUserId, selectedReport.reportedUserName)}
                    >
                      Unban User
                    </button>
                  ) : (
                    <button 
                      className="btn btn-danger"
                      onClick={() => handleBanUser(selectedReport.reportedUserId, selectedReport.reportedUserName)}
                    >
                      Ban from Chat
                    </button>
                  )}
                </div>
              </div>
            </div>
            </div>
          ) : (
            <div className="report-detail report-detail-empty">
              <h2>Select a report</h2>
              <p>Choose a report from the list to review details, history, notes, and moderation actions.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports;
